import Modal from '../../../Modal/ModalBasic';
import NotificationBase from '../../../Notifications/NotificationBase';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useDispatch, useSelector } from 'react-redux';
import { EScheduleSteps, updateScheduleSteps } from '../../../../../features/schedule/schedule-slice';
import { clearCalendarSelected } from '../../../../../features/calendar/calendar-slice';
import { useCancelScheduleByIdMutation } from '../../../../../services/schedule.service';
import { ISchedules } from '../../../../../services/journey.service';
import { RootState } from '../../../../../store';

const SuccessfulCancelContent = () => (
  <Typography variant="body1">
    Agradecemos por nos informar, seu horário está disponível novamente. Esperamos poder atendê-la em uma próxima
    oportunidade.
  </Typography>
);

const NoAppointmentsContent = ({ handleUpdateScheduleSteps }: { handleUpdateScheduleSteps: () => void }) => {
  const {
    calendar: { calendarsAvailables }
  } = useSelector((state: RootState) => state);
  return (
    <>
      <Typography variant="body2R" sx={{ mb: 3 }}>
        Não encontramos horários disponíveis para o seu agendamento, tente novamente mais tarde ou entre em contato com a
        nossa equipe.
      </Typography>

      {calendarsAvailables.length > 1 ? (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button variant="contained" color="secondary" onClick={handleUpdateScheduleSteps}>
            Voltar
          </Button>
          <Button
            variant="inline"
            color="primary"
            endIcon={<WhatsAppIcon />}
            href={process.env.REACT_APP_WHATSAPP || ''}
            target="_blank"
            sx={{
              display: 'flex',
              alignItems: 'baseline'
            }}
          >
            Entrar em contato
          </Button>
        </Box>
      ) : (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="inline"
            color="primary"
            endIcon={<WhatsAppIcon />}
            href={process.env.REACT_APP_WHATSAPP || ''}
            target="_blank"
          >
            Entrar em contato
          </Button>
        </Box>
      )}
    </>
  );
};

const ActionOptionsContent = ({
  doctorName,
  onKeepAppointment,
  onCancelAppointment,
  isLoading
}: {
  doctorName: string;
  onKeepAppointment: () => void;
  onCancelAppointment: () => void;
  isLoading: boolean;
}) => (
  <>
    <Typography variant="body1">
      Não encontramos horários disponíveis para o seu reagendamento, deseja manter seu encontro com a Dra {doctorName}? ou
      cancelar a consulta?
    </Typography>

    <NotificationBase sx={{ alignItems: 'center' }}>
      <Typography variant="body2R">Ao cancelar, seu horário ficará disponível para outras Oyanas.</Typography>
    </NotificationBase>

    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
      <Button variant="contained" onClick={onKeepAppointment}>
        Voltar e manter horário
      </Button>
      <LoadingButton loading={isLoading} variant="contained" color="secondary" onClick={onCancelAppointment}>
        Cancelar agendamento
      </LoadingButton>
    </Box>
  </>
);

const useAppointmentsModalLogic = (schedule: ISchedules) => {
  const dispatch = useDispatch();
  const [cancelScheduleById, { isLoading, isSuccess }] = useCancelScheduleByIdMutation();
  const {
    schedule: {
      ui: { previousStep }
    }
  } = useSelector((state: RootState) => state);

  const handleClose = () => {
    dispatch(updateScheduleSteps({ currentStep: EScheduleSteps.idle, previousStep: EScheduleSteps.idle }));
    dispatch(clearCalendarSelected());
  };

  const handleUpdateScheduleSteps = () => {
    dispatch(updateScheduleSteps({ currentStep: previousStep, previousStep: EScheduleSteps.idle }));
  };

  const handleKeepAppointment = () => {
    handleClose();
  };

  const handleCancelAppointment = () => {
    if (schedule?.id) {
      cancelScheduleById({ id: schedule.id });
    }
  };

  return {
    isLoading,
    isSuccess,
    handleClose,
    handleUpdateScheduleSteps,
    handleKeepAppointment,
    handleCancelAppointment
  };
};

export function ReturnModalAppointmentsNotFound({ schedule }: { schedule: ISchedules }) {
  const {
    isLoading,
    isSuccess: isCancelSuccess,
    handleClose,
    handleUpdateScheduleSteps,
    handleKeepAppointment,
    handleCancelAppointment
  } = useAppointmentsModalLogic(schedule);

  const getModalTitle = () => {
    if (isCancelSuccess) return 'Consulta cancelada';
    return 'Ops!';
  };

  const renderModalContent = () => {
    if (isCancelSuccess) {
      return <SuccessfulCancelContent />;
    }

    if (schedule.allowReschedule) {
      return (
        <ActionOptionsContent
          doctorName={schedule.user.name}
          onKeepAppointment={handleKeepAppointment}
          onCancelAppointment={handleCancelAppointment}
          isLoading={isLoading}
        />
      );
    }

    return <NoAppointmentsContent handleUpdateScheduleSteps={handleUpdateScheduleSteps} />;
  };

  return (
    <Modal open={true} onCloseHandler={handleClose} title={getModalTitle()} typeTitle="h3">
      {renderModalContent()}
    </Modal>
  );
}
